// Info Panel component - displays segment content
import { appState } from "../utils/appState";
import { i18n } from "../utils/i18n";
import { analytics } from "../utils/analytics";
import { parseMarkdown } from "../utils/markdown";

export class InfoPanel {
  private container: HTMLElement;
  private isVisible = false;

  // Cache DOM elements
  private titleEl: HTMLElement | null = null;
  private bodyEl: HTMLElement | null = null;
  private footerEl: HTMLElement | null = null;
  private learnMoreLink: HTMLAnchorElement | null = null;
  private learnMoreText: HTMLElement | null = null;
  private closeBtn: HTMLElement | null = null;

  constructor(containerSelector: string) {
    const element = document.querySelector(containerSelector);
    if (!element) throw new Error(`Element "${containerSelector}" not found`);

    this.container = element as HTMLElement;
  }

  init(): void {
    this.cacheElements();
    this.setupStateListeners();
    this.setupCloseHandlers();
  }

  private cacheElements(): void {
    this.titleEl = this.container.querySelector(".info-panel-title");
    this.bodyEl = this.container.querySelector(".info-panel-body");
    this.footerEl = this.container.querySelector(".info-panel-footer");
    this.learnMoreLink = this.container.querySelector(".info-panel-learn-more");
    this.learnMoreText = this.container.querySelector(
      ".info-panel-learn-more-text"
    );
    this.closeBtn = this.container.querySelector(".info-panel-close");
  }

  private setupStateListeners(): void {
    appState.activeSegment.subscribe((segment) => {
      if (segment) {
        this.show(segment.type, segment.key);
      } else {
        this.hide();
      }
    });

    window.addEventListener("localechange", () => {
      const segment = appState.activeSegment.get();
      if (segment) {
        this.updateContent(segment.type, segment.key);
      }
    });
  }

  private setupCloseHandlers(): void {
    this.closeBtn?.addEventListener("click", () => {
      analytics.trackPanelClose("button");
      appState.activeSegment.set(null);
    });

    this.learnMoreLink?.addEventListener("click", () => {
      const segment = appState.activeSegment.get();
      if (segment) {
        analytics.trackLearnMoreClick(segment.key);
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isVisible) {
        analytics.trackPanelClose("escape");
        appState.activeSegment.set(null);
      }
    });
  }

  private show(segmentType: string, segmentKey: string): void {
    this.updateContent(segmentType, segmentKey);

    // Add panel-open class first to set up grid layout
    const appContainer = document.querySelector(".app-container");
    appContainer?.classList.add("panel-open");

    // Then trigger visibility transition in next frame

    requestAnimationFrame(() => {
      this.container.classList.add("visible");
      this.isVisible = true;

      // Only auto-focus close button if opened via keyboard
      const openedViaKeyboard = appState.openedViaKeyboard.get();
      if (openedViaKeyboard) {
        setTimeout(() => {
          this.closeBtn?.focus();
        }, 400);
      }
    });
  }

  private hide(): void {
    this.container.classList.remove("visible");

    // Remove class from app-container immediately for smooth synchronized animation
    const appContainer = document.querySelector(".app-container");
    appContainer?.classList.remove("panel-open");

    this.isVisible = false;
  }

  private updateContent(segmentType: string, segmentKey: string): void {
    // Handle 'about' type separately
    if (segmentType === 'about') {
      this.showAboutContent();
      return;
    }

    const contentType =
      segmentType === "principle" ? "principles" : "foundations";
    const content = i18n.getSegmentContent(contentType, segmentKey);

    if (!content) {
      this.showError();
      return;
    }

    // Update title
    if (this.titleEl) {
      this.titleEl.textContent = content.title;
    }

    // Update body with markdown parsing
    if (this.bodyEl) {
      this.bodyEl.innerHTML = parseMarkdown(content.description);
    }

    // Update close button aria-label
    if (this.closeBtn) {
      this.closeBtn.setAttribute(
        "aria-label",
        i18n.getString("ui.closeButton")
      );
    }

    // Update learn more link
    if (
      content.learnMoreUrl &&
      this.footerEl &&
      this.learnMoreLink &&
      this.learnMoreText
    ) {
      this.learnMoreLink.href = content.learnMoreUrl;
      this.learnMoreText.textContent =
        content.learnMoreText || i18n.getString("ui.learnMoreDefault");
      this.footerEl.style.display = "";
    } else if (this.footerEl) {
      this.footerEl.style.display = "none";
    }
  }

  private showAboutContent(): void {
    // Update title
    if (this.titleEl) {
      this.titleEl.textContent = i18n.getString('ui.aboutTitle');
    }

    // Update body with description
    if (this.bodyEl) {
      this.bodyEl.innerHTML = parseMarkdown(i18n.getString('ui.aboutDescription'));
    }

    // Update close button aria-label
    if (this.closeBtn) {
      this.closeBtn.setAttribute(
        "aria-label",
        i18n.getString("ui.closeButton")
      );
    }

    // Update learn more link to point to the white paper PDF
    if (this.footerEl && this.learnMoreLink && this.learnMoreText) {
      this.learnMoreLink.href = i18n.getString('ui.aboutDownloadUrl');
      this.learnMoreText.textContent = i18n.getString('ui.aboutDownloadButton');
      this.footerEl.style.display = "";
    }
  }

  private showError(): void {
    // Show error in the body
    if (this.titleEl) {
      this.titleEl.textContent = i18n.getString("ui.errorTitle");
    }
    if (this.bodyEl) {
      this.bodyEl.textContent = i18n.getString("ui.errorMessage");
    }
    if (this.footerEl) {
      this.footerEl.style.display = "none";
    }
  }
}
